import type { LocationQuery } from "vue-router";
import type { Module } from "vuex";

import type { PublishedStatus } from "hoxton-types";
import { PLATFORM } from "hoxton-shared/enums/exports";

import type { MetadataField, ConstructedDeliverable } from "@/types/types";

import { BannerQaStatus, BannerQaUserStatus } from "@/enums/banners";
import {
    LibraryDrawerTab,
    PreviewDrawerTab,
    UrlFilterParams,
    ViewMode,
    ViewModeDefaultPageSize
} from "@/enums/library";
import { formatGroupName } from "@/utils";
import type { RootState } from "@/store/types";
import type { BannerQaStatusFilter } from "@/enums/filters";

const storeNamespace = "deliverableLibrary";

const DeliverableLibraryInternalAction = {
    SetInitialState: "setInitialState"
};

export const DeliverableLibraryAction = {
    SetDeliverableStatusByIdHash: `${storeNamespace}/setDeliverableStatusByIdHash`,
    UnsetDeliverableStatusByIdHash: `${storeNamespace}/unsetDeliverableStatusByIdHash`,
    SetInitialState: `${storeNamespace}/${DeliverableLibraryInternalAction.SetInitialState}`,
    SetCampaignId: `${storeNamespace}/setCampaignId`,
    SetTableSortedColumnKeys: `${storeNamespace}/setTableSortedColumnKeys`,
    SetTableVisibleColumnKeys: `${storeNamespace}/setTableVisibleColumnKeys`,
    SetCreativeValuesAreVisible: `${storeNamespace}/setCreativeValuesAreVisible`,
    ClearMetadataFieldsByFieldId: `${storeNamespace}/clearMetadataFieldsByFieldId`,
    SetMetadataFieldsByFieldId: `${storeNamespace}/setMetadataFieldsByFieldId`,
    UnsetMetadataFieldsByFieldId: `${storeNamespace}/unsetMetadataFieldsByFieldId`,
    SetPagination: `${storeNamespace}/setPagination`,
    SetPreviewDrawerActiveTab: `${storeNamespace}/setPreviewDrawerActiveTab`,
    SetDrawerActiveTab: `${storeNamespace}/setDrawerActiveTab`,
    SetDrawerDeliverables: `${storeNamespace}/setDrawerDeliverables`,
    SetDrawerIsVisible: `${storeNamespace}/setDrawerIsVisible`,
    SetEditableGroupFilterOptions: `${storeNamespace}/setEditableGroupFilterOptions`,
    SetLanguageFilterOptions: `${storeNamespace}/setLanguageFilterOptions`,
    SetMasterTemplateFilterOptions: `${storeNamespace}/setMasterTemplateFilterOptions`,
    SetViewMode: `${storeNamespace}/setViewMode`
};

export const DeliverableLibraryGetters = {
    allDeliverablesValues: `${storeNamespace}/allDeliverablesValues`,
    computedDeliverableStatus: `${storeNamespace}/computedDeliverableStatus`,
    computedDeliverableIsPublishedToPlatform: `${storeNamespace}/computedDeliverableIsPublishedToPlatform`,
    computedDeliverableIsPublishedToDoubleClick: `${storeNamespace}/computedDeliverableIsPublishedToDoubleClick`,
    computedDeliverableIsPublishedToODC: `${storeNamespace}/computedDeliverableIsPublishedToODC`,
    urlFilterParams: `${storeNamespace}/urlFilterParams`,
    tableColumnLabelsByColumnKey: `${storeNamespace}/tableColumnLabelsByColumnKey`,
    getLoggedInUserStatusForIdHash: `${storeNamespace}/getLoggedInUserStatusForIdHash`
};

export const DeliverableLibraryInternalGetters = {
    computedDeliverableStatus: "computedDeliverableStatus"
};

export const DeliverableLibraryMutation = {
    ResetState: "resetState",
    SetCampaignId: "setCampaignId",
    SetCreativeValuesAreVisible: "setCreativeValuesAreVisible",
    UnsetDeliverableStatusByIdHash: "unsetDeliverableStatusByIdHash",
    SetDeliverableStatusByIdHash: "setDeliverableStatusByIdHash",
    ClearMetadataFieldsByFieldId: "clearMetadataFieldsByFieldId",
    SetMetadataFieldsByFieldId: "setMetadataFieldsByFieldId",
    unsetMetadataFieldsByFieldId: "unsetMetadataFieldsByFieldId",
    SetPagination: "setPagination",
    SetTableSortedColumnKeys: "setTableSortedColumnKeys",
    SetTableVisibleColumnKeys: "setTableVisibleColumnKeys",
    SetPreviewDrawerActiveTab: "setPreviewDrawerActiveTab",
    SetDrawerActiveTab: "setDrawerActiveTab",
    SetDrawerDeliverables: "setDrawerDeliverables",
    SetDrawerIsVisible: "setDrawerIsVisible",
    SetEditableGroupFilterOptions: "setEditableGroupFilterOptions",
    SetLanguageFilterOptions: "setLanguageFilterOptions",
    SetMasterTemplateFilterOptions: "setMasterTemplateFilterOptions",
    SetViewMode: "setViewMode"
};

const generatePaginationDefaults = () => ({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0
});

const viewModeDefault = ViewMode.Grid;

type BannerQaStatusType = (typeof BannerQaStatusFilter)[keyof typeof BannerQaStatusFilter];
type BannerQaUserStatusType = (typeof BannerQaUserStatus)[keyof typeof BannerQaUserStatus];

type LibraryDrawerTabType = (typeof LibraryDrawerTab)[keyof typeof LibraryDrawerTab];
type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode];

type PlatformType = (typeof PLATFORM)[keyof typeof PLATFORM];

type PlatformPublishedStatus = { xmlFeedUrl?: string | null };

const getPublishedStatusForPlatform = (
    publishedStatus: PublishedStatus,
    platform: PlatformType
): PlatformPublishedStatus | null => {
    if (!publishedStatus) {
        return null;
    }

    if ("creativex" in publishedStatus) {
        return platform === PLATFORM.CREATIVEX ? publishedStatus.creativex : null;
    }

    if (platform === PLATFORM.CREATIVEX) {
        return null;
    }

    const publishedStatusRecord = publishedStatus as Record<string, PlatformPublishedStatus>;

    return publishedStatusRecord[platform] ?? null;
};

type DeliverableStatus = {
    publishedStatus: PublishedStatus;
    status: BannerQaStatusType;
    users?: ConstructedDeliverable["users"];
    visibleTo: ConstructedDeliverable["visibleTo"];
};

export type State = {
    campaignId: string | null;
    deliverableStatusByIdHash: Record<string, DeliverableStatus>;
    drawer: {
        activeTab: LibraryDrawerTabType | null;
        deliverables: ConstructedDeliverable[] | null;
        isVisible: boolean;
    };
    metadataFieldsByFieldId: Record<string, MetadataField>;
    pagination: ReturnType<typeof generatePaginationDefaults>;
    previewDrawerTab: {
        activeTab: (typeof PreviewDrawerTab)[keyof typeof PreviewDrawerTab] | null;
    };
    table: {
        creativeValuesAreVisible: boolean;
        sortedColumnKeys: string[] | null;
        visibleColumnKeys: string[] | null;
    };
    viewMode: ViewModeType;
    filterOptions: {
        editableGroups: {
            label: string;
            name: string;
            options: { value: string; label: string }[];
        }[];
        languages: { value: string; label: string }[];
        masterTemplates: { value: string; label: string; width: number; height: number }[];
    };
};

type Getters = {
    computedDeliverableIsPublishedToPlatform: (idHash: string, platform: PlatformType) => boolean;
    computedDeliverableIsPublishedToDoubleClick: (idHash: string) => boolean;
    computedDeliverableIsPublishedToODC: (idHash: string) => boolean;
    computedDeliverableStatus: (idHash: string) => BannerQaStatusType;
    computedDeliverableIsPublishedToSizmek: (idHash: string) => boolean;
    getLoggedInUserStatusForIdHash: (idHash: string) => BannerQaUserStatusType | null;
    urlFilterParams: {
        key: string;
        value: string[];
    }[];
};

const getDefaultState = (): State => ({
    campaignId: null,
    deliverableStatusByIdHash: {},
    drawer: {
        activeTab: null,
        deliverables: [],
        isVisible: false
    },
    metadataFieldsByFieldId: {},
    pagination: generatePaginationDefaults(),
    previewDrawerTab: {
        activeTab: null
    },
    table: {
        creativeValuesAreVisible: false,
        sortedColumnKeys: null,
        visibleColumnKeys: null
    },
    viewMode: viewModeDefault,
    filterOptions: {
        editableGroups: [],
        languages: [],
        masterTemplates: []
    }
});

const deliverableLibraryModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),

    getters: {
        computedDeliverableIsPublishedToPlatform: state => (idHash: string, platform: PlatformType) => {
            const publishedStatus = state.deliverableStatusByIdHash[idHash]?.publishedStatus;

            if (!publishedStatus) {
                return false;
            }

            const platformStatus = getPublishedStatusForPlatform(publishedStatus, platform);

            return platformStatus?.xmlFeedUrl != null;
        },

        computedDeliverableIsPublishedToDoubleClick: (state, getters: Getters) => (idHash: string) =>
            getters.computedDeliverableIsPublishedToPlatform(idHash, PLATFORM.DOUBLECLICK),

        computedDeliverableIsPublishedToODC: (state, getters: Getters) => (idHash: string) =>
            getters.computedDeliverableIsPublishedToPlatform(idHash, PLATFORM.OPENDC),

        computedDeliverableStatus: state => (idHash: string) => {
            if (state.deliverableStatusByIdHash[idHash]) {
                return state.deliverableStatusByIdHash[idHash].status;
            }

            return BannerQaStatus.Draft;
        },

        getLoggedInUserStatusForIdHash(state, getters, rootState) {
            return (idHash: string) => {
                const userId = rootState.auth.me?.id;
                const deliverable = state.deliverableStatusByIdHash[idHash];

                if (!deliverable) {
                    return null;
                }

                const usersStatuses = deliverable.users;

                if (usersStatuses?.length) {
                    const userStatus = usersStatuses.find(status => status.user.id === userId);

                    if (userStatus) {
                        return userStatus.status;
                    }
                }
                return null;
            };
        },

        tableColumnLabelsByColumnKey(state, getters, rootState) {
            const groupLabelsByGroupName = rootState.campaign.normalized.editableGroupIds.reduce<
                Record<string, string>
            >((acc, groupName) => {
                acc[groupName] = formatGroupName(groupName);
                return acc;
            }, {});

            const metadataFieldLabelsByFieldId = Object.keys(state.metadataFieldsByFieldId).reduce<
                Record<string, string>
            >((acc, fieldId) => {
                if (!state.metadataFieldsByFieldId[fieldId]) {
                    return acc;
                }

                acc[fieldId] = state.metadataFieldsByFieldId[fieldId].name;
                return acc;
            }, {});
            /*
                This object defines the default sort order.
            */
            return {
                status: "QA Status",
                publishedStatus: "Published",
                templateSize: "Ad Size",
                language: "Language",
                ...groupLabelsByGroupName,
                ...metadataFieldLabelsByFieldId
            };
        }
    },

    mutations: {
        resetState(state) {
            state.pagination = generatePaginationDefaults();
            state.viewMode = viewModeDefault;
            state.table.sortedColumnKeys = null;
            state.table.visibleColumnKeys = null;
        },

        setCampaignId(state, campaignId: State["campaignId"]) {
            state.campaignId = campaignId;
        },

        setPagination(state, pagination: State["pagination"]) {
            state.pagination = pagination;
        },

        setViewMode(state, viewMode: State["viewMode"]) {
            state.viewMode = viewMode;
        },

        setDrawerActiveTab(state, activeTab: State["drawer"]["activeTab"]) {
            state.drawer.activeTab = activeTab;
        },

        setDrawerDeliverables(state, deliverables: ConstructedDeliverable[]) {
            state.drawer.deliverables = deliverables;
        },

        setDrawerIsVisible(state, isVisible: State["drawer"]["isVisible"]) {
            state.drawer.isVisible = isVisible;
        },

        setPreviewDrawerActiveTab(state, activeTab: State["previewDrawerTab"]["activeTab"]) {
            state.previewDrawerTab.activeTab = activeTab;
        },

        setTableSortedColumnKeys(state, sortedColumnKeys: string[]) {
            state.table.sortedColumnKeys = [...sortedColumnKeys];
        },

        setTableVisibleColumnKeys(state, visibleColumnKeys: string[]) {
            state.table.visibleColumnKeys = [...visibleColumnKeys];
        },

        setCreativeValuesAreVisible(state, creativeValuesAreVisible: State["table"]["creativeValuesAreVisible"]) {
            state.table.creativeValuesAreVisible = creativeValuesAreVisible;
        },

        setDeliverableStatusByIdHash(state, deliverables: ConstructedDeliverable[]) {
            deliverables.forEach(deliverable => {
                const idHash = deliverable.idHash || deliverable.hash;
                const existingDeliverableStatus = state.deliverableStatusByIdHash[idHash];

                if (idHash) {
                    /*
                        As the deliverable data can sometimes come back incomplete from the BE (publishing
                        mutations), or for situations where we only want to set or update a single field,
                        we fall back to any current data we have for the deliverable and then finally
                        to defaults.
                    */
                    /*
                        Because publishedStatus could intentionally be null we do an explicit
                        check for it before allowing us to fall back to previous or default
                        values.
                    */
                    const publishedStatus =
                        deliverable.publishedStatus === null
                            ? deliverable.publishedStatus
                            : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                              (deliverable.publishedStatus ?? existingDeliverableStatus?.publishedStatus ?? null);

                    state.deliverableStatusByIdHash[idHash] = {
                        status: deliverable.status ?? existingDeliverableStatus?.status ?? BannerQaStatus.Draft,
                        publishedStatus,
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                        users: deliverable.users ?? existingDeliverableStatus?.users ?? [],
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                        visibleTo: deliverable.visibleTo ?? existingDeliverableStatus?.visibleTo ?? []
                    };
                }
            });
        },

        unsetDeliverableStatusByIdHash(state, deliverables: ConstructedDeliverable[]) {
            deliverables.forEach(deliverable => {
                delete state.deliverableStatusByIdHash[deliverable.idHash];
            });
        },

        clearMetadataFieldsByFieldId(state) {
            state.metadataFieldsByFieldId = {};
        },

        setMetadataFieldsByFieldId(state, metadataField: MetadataField) {
            state.metadataFieldsByFieldId[metadataField._id] = metadataField;
        },

        unsetMetadataFieldsByFieldId(state, metadataFieldId: string) {
            state.metadataFieldsByFieldId = {
                ...state.metadataFieldsByFieldId
            };
            delete state.metadataFieldsByFieldId[metadataFieldId];
        },

        setEditableGroupFilterOptions(state, options: State["filterOptions"]["editableGroups"]) {
            state.filterOptions.editableGroups = options;
        },

        setLanguageFilterOptions(state, options: State["filterOptions"]["languages"]) {
            state.filterOptions.languages = options;
        },

        setMasterTemplateFilterOptions(state, options: State["filterOptions"]["masterTemplates"]) {
            state.filterOptions.masterTemplates = options;
        }
    },

    actions: {
        setDeliverableStatusByIdHash({ commit }, deliverables) {
            commit(DeliverableLibraryMutation.SetDeliverableStatusByIdHash, deliverables);
        },

        unsetDeliverableStatusByIdHash({ commit }, deliverables) {
            commit(DeliverableLibraryMutation.UnsetDeliverableStatusByIdHash, deliverables);
        },

        setInitialState({ commit, state, rootState }, query: LocationQuery) {
            const setPageAndViewModeState = () => {
                if (query[UrlFilterParams.Page]) {
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        currentPage: Number(query[UrlFilterParams.Page])
                    });
                }
                if (query[UrlFilterParams.ViewMode]) {
                    commit(DeliverableLibraryMutation.SetViewMode, query[UrlFilterParams.ViewMode]);
                }
                if (state.viewMode === ViewMode.Grid) {
                    /*
                        We only allow a single page size in grid view, so we'll hard code it.
                    */
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: ViewModeDefaultPageSize[ViewMode.Grid]
                    });
                } else if (query[UrlFilterParams.PageSize]) {
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: Number(query[UrlFilterParams.PageSize])
                    });
                } else {
                    /*
                        If we're not in grid mode and we have no page size set
                        in the query param then let's assume that we're in table
                        mode, so we'll set the default table page size.
                    */
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: ViewModeDefaultPageSize[ViewMode.Table]
                    });
                }
            };

            commit(DeliverableLibraryMutation.ResetState);
            commit(DeliverableLibraryMutation.SetCampaignId, rootState.campaign._id);
            if (query[UrlFilterParams.DeliverableDrawerIsVisible]) {
                commit(DeliverableLibraryMutation.SetDrawerIsVisible, true);
            }
            setPageAndViewModeState();
        },

        setCampaignId({ commit }, campaignId: State["campaignId"]) {
            commit(DeliverableLibraryMutation.SetCampaignId, campaignId);
        },

        setCreativeValuesAreVisible({ commit }, creativeValuesAreVisible: State["table"]["creativeValuesAreVisible"]) {
            commit(DeliverableLibraryMutation.SetCreativeValuesAreVisible, creativeValuesAreVisible);
        },

        clearMetadataFieldsByFieldId({ commit }) {
            commit(DeliverableLibraryMutation.ClearMetadataFieldsByFieldId);
        },

        setMetadataFieldsByFieldId({ commit }, metadataField: MetadataField) {
            commit(DeliverableLibraryMutation.SetMetadataFieldsByFieldId, metadataField);
        },

        unsetMetadataFieldsByFieldId({ commit }, metadataFieldId: string) {
            commit(DeliverableLibraryMutation.unsetMetadataFieldsByFieldId, metadataFieldId);
        },

        setTableSortedColumnKeys({ commit }, sortedColumnKeys: State["table"]["sortedColumnKeys"]) {
            commit(DeliverableLibraryMutation.SetTableSortedColumnKeys, sortedColumnKeys);
        },

        setTableVisibleColumnKeys({ commit }, visibleColumnKeys: State["table"]["visibleColumnKeys"]) {
            commit(DeliverableLibraryMutation.SetTableVisibleColumnKeys, visibleColumnKeys);
        },

        setPagination({ commit }, pagination: State["pagination"]) {
            commit(DeliverableLibraryMutation.SetPagination, pagination);
        },

        setPreviewDrawerActiveTab({ commit }, activeTab: State["previewDrawerTab"]["activeTab"]) {
            commit(DeliverableLibraryMutation.SetPreviewDrawerActiveTab, activeTab);
        },

        setViewMode({ commit }, viewMode: State["viewMode"]) {
            commit(DeliverableLibraryMutation.SetViewMode, viewMode);
        },

        setDrawerActiveTab({ commit }, activeTab: State["drawer"]["activeTab"]) {
            commit(DeliverableLibraryMutation.SetDrawerActiveTab, activeTab);
        },

        setDrawerDeliverables({ commit }, deliverables: ConstructedDeliverable[]) {
            commit(DeliverableLibraryMutation.SetDrawerDeliverables, deliverables);
        },

        setDrawerIsVisible({ commit }, isVisible: State["drawer"]["isVisible"]) {
            commit(DeliverableLibraryMutation.SetDrawerIsVisible, isVisible);
        },

        setEditableGroupFilterOptions({ commit }, options: State["filterOptions"]["editableGroups"]) {
            commit(DeliverableLibraryMutation.SetEditableGroupFilterOptions, options);
        },

        setLanguageFilterOptions({ commit }, options: State["filterOptions"]["languages"]) {
            commit(DeliverableLibraryMutation.SetLanguageFilterOptions, options);
        },

        setMasterTemplateFilterOptions({ commit }, options: State["filterOptions"]["masterTemplates"]) {
            commit(DeliverableLibraryMutation.SetMasterTemplateFilterOptions, options);
        }
    }
};

export default deliverableLibraryModule;
